import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, Lock, User, Loader2, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { springPresets } from '@/lib/motion';

type Mode = 'signin' | 'signup' | 'forgot';

export default function AuthModal() {
  const { signIn, signUp, resetPassword, setShowAuthModal } = useAuth();
  const [mode,      setMode]      = useState<Mode>('signin');
  const [email,     setEmail]     = useState('');
  const [password,  setPassword]  = useState('');
  const [name,      setName]      = useState('');
  const [showPw,    setShowPw]    = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');
  const [success,   setSuccess]   = useState('');

  const close = () => setShowAuthModal(false);
  const switchMode = (m: Mode) => { setMode(m); setError(''); setSuccess(''); };

  const handleSubmit = async () => {
    setError(''); setSuccess('');

    if (mode === 'forgot') {
      if (!email.trim()) { setError('Please enter your email address.'); return; }
      setLoading(true);
      const err = await resetPassword(email.trim());
      setLoading(false);
      if (err) setError(err);
      else setSuccess('Reset link sent! Check your inbox and follow the link.');
      return;
    }

    if (!email.trim() || !password.trim()) { setError('Email and password are required.'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    setLoading(true);
    if (mode === 'signup') {
      const err = await signUp(email.trim(), password, name.trim() || undefined);
      if (err) { setError(err); }
      else {
        setSuccess('Account created! Check your email to confirm, then sign in.');
        setTimeout(() => switchMode('signin'), 2500);
      }
    } else {
      const err = await signIn(email.trim(), password);
      if (err) { setError(err); }
      else { close(); }
    }
    setLoading(false);
  };

  const title = mode === 'signin' ? 'Welcome back 👋' : mode === 'signup' ? 'Create account 🌿' : 'Reset password 🔑';
  const subtitle = mode === 'signin'
    ? 'Sign in to sync your saved meals & lists'
    : mode === 'signup'
    ? 'Save your meal plans across all devices'
    : 'Enter your email and we\'ll send a reset link';

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={e => { if (e.target === e.currentTarget) close(); }}
    >
      <motion.div
        initial={{ y: '100%', opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: '100%', opacity: 0 }}
        transition={springPresets.snappy}
        className="bg-background w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl p-6 pb-10 sm:pb-6"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            {mode === 'forgot' && (
              <button onClick={() => switchMode('signin')} className="text-muted-foreground hover:text-foreground p-1 -ml-1 mr-1">
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            <div>
              <h2 className="text-xl font-bold text-foreground">{title}</h2>
              <p className="text-sm text-muted-foreground mt-0.5">{subtitle}</p>
            </div>
          </div>
          <button onClick={close} className="text-muted-foreground hover:text-foreground p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-3">
          {mode === 'signup' && (
            <div className="relative">
              <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text" placeholder="Display name (optional)" value={name}
                onChange={e => setName(e.target.value)}
                className="w-full h-12 pl-10 pr-4 rounded-xl border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
          )}

          <div className="relative">
            <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="email" placeholder="Email address" value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSubmit()}
              className="w-full h-12 pl-10 pr-4 rounded-xl border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>

          {mode !== 'forgot' && (
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type={showPw ? 'text' : 'password'} placeholder="Password (6+ characters)" value={password}
                onChange={e => setPassword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                className="w-full h-12 pl-10 pr-11 rounded-xl border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
              <button type="button" onClick={() => setShowPw(v => !v)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          )}

          {/* Forgot password link — only in signin mode */}
          {mode === 'signin' && (
            <div className="text-right -mt-1">
              <button onClick={() => switchMode('forgot')}
                className="text-xs text-muted-foreground hover:text-primary transition-colors">
                Forgot password?
              </button>
            </div>
          )}

          <AnimatePresence mode="wait">
            {error && (
              <motion.p key="err" initial={{opacity:0,y:-4}} animate={{opacity:1,y:0}} exit={{opacity:0}}
                className="text-sm text-destructive font-medium">⚠ {error}</motion.p>
            )}
            {success && (
              <motion.p key="ok" initial={{opacity:0,y:-4}} animate={{opacity:1,y:0}} exit={{opacity:0}}
                className="text-sm text-primary font-medium">✓ {success}</motion.p>
            )}
          </AnimatePresence>

          <button onClick={handleSubmit} disabled={loading}
            className="w-full h-12 rounded-xl bg-primary text-primary-foreground font-bold text-sm flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-60 transition-opacity mt-1">
            {loading
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : mode === 'signin' ? 'Sign In' : mode === 'signup' ? 'Create Account' : 'Send Reset Link'}
          </button>

          {mode !== 'forgot' && (
            <p className="text-center text-sm text-muted-foreground pt-1">
              {mode === 'signin' ? "Don't have an account? " : 'Already have an account? '}
              <button onClick={() => switchMode(mode === 'signin' ? 'signup' : 'signin')}
                className="text-primary font-semibold hover:underline">
                {mode === 'signin' ? 'Sign up free' : 'Sign in'}
              </button>
            </p>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
